import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { SiteHeader, SiteFooter } from '@/components/SiteLayout';
import { API } from '@/App';
import axios from 'axios';
import {
  ArrowRight, Shield, Zap, Globe, CheckCircle, Code, CreditCard,
  Lock, Users, BarChart3, Terminal, Webhook, Copy, Check,
  ChevronRight, Star, Layers, RefreshCcw, Wallet
} from 'lucide-react';

const CODE_SNIPPET = `// Create a payment invoice
const response = await fetch('https://api.coinpayup.com/api/invoices', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer YOUR_API_KEY',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    amount: 49.99,
    currency: 'USDT',
    description: 'Premium Plan'
  })
});

const { id, wallet_address, payable_amount } = await response.json();
// Redirect customer to hosted checkout
window.location = \`https://coinpayup.com/checkout/\${id}\`;`;

const TESTIMONIALS = [
  { name: 'Alex Rivera', role: 'CTO, StreamFi', quote: 'CoinPayUp replaced our entire payment stack. Integration took 20 minutes and we haven\'t looked back. Zero chargebacks in 6 months.', avatar: 'AR' },
  { name: 'Sarah Chen', role: 'Founder, NovaPay Labs', quote: 'The non-custodial approach was the dealbreaker. Our funds go directly to our wallet — no middleman, no delays. Exactly what we needed.', avatar: 'SC' },
  { name: 'Marcus Johnson', role: 'Lead Dev, BlockCart', quote: 'Best API docs I\'ve seen in crypto. We had webhooks firing in production within an hour. The team\'s response time is incredible.', avatar: 'MJ' },
];

const LandingPage = () => {
  const [plans, setPlans] = useState([]);
  const [copied, setCopied] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    document.title = 'CoinPayUp | Accept Crypto Payments on Your Website Instantly';
    axios.get(`${API}/plans/public`).then(res => setPlans(res.data)).catch(() => {});

    // ─── JSON-LD: Organization + WebSite + SoftwareApplication ───
    const schemas = [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": "CoinPayUp",
        "url": "https://coinpayup.com",
        "logo": "https://coinpayup.com/favicon.ico",
        "description": "Start accepting crypto payments directly on your website with CoinPayUp — built for online businesses, e-commerce stores, SaaS platforms, and digital services.",
        "contactPoint": { "@type": "ContactPoint", "contactType": "customer support", "url": "https://coinpayup.com/support" }
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        "name": "CoinPayUp",
        "url": "https://coinpayup.com",
        "description": "Accept crypto payments instantly on your website or store. No middlemen, no chargebacks."
      },
      {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "CoinPayUp",
        "applicationCategory": "FinanceApplication",
        "operatingSystem": "Web",
        "url": "https://coinpayup.com",
        "description": "Non-custodial cryptocurrency payment gateway for merchants. Multi-chain support: BSC, ETH, Polygon, Tron.",
        "featureList": "Multi-chain support, Real-time blockchain verification, Webhook notifications, REST API, No chargebacks",
        "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
      }
    ];

    const scriptEls = schemas.map(schema => {
      const el = document.createElement('script');
      el.type = 'application/ld+json';
      el.setAttribute('data-schema', `landing-${schema['@type']}`);
      el.text = JSON.stringify(schema);
      document.head.appendChild(el);
      return el;
    });
    return () => scriptEls.forEach(el => el.remove());
  }, []);

  // Inject OfferCatalog JSON-LD once plans load
  useEffect(() => {
    if (!plans.length) return;
    const offerSchema = {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "name": "CoinPayUp Pricing Plans",
      "description": "Simple, transparent pricing for crypto payment processing",
      "url": "https://coinpayup.com/#pricing",
      "itemListElement": plans.map((plan, i) => ({
        "@type": "ListItem",
        "position": i + 1,
        "item": {
          "@type": "Offer",
          "name": plan.name,
          "description": `${plan.tx_limit ?? 'Unlimited'} verified TX/month. ${plan.percent_fee}% per transaction fee.`,
          "price": plan.monthly_price ?? 0,
          "priceCurrency": "USD",
          "url": `https://coinpayup.com/#pricing`
        }
      }))
    };
    const el = document.createElement('script');
    el.type = 'application/ld+json';
    el.setAttribute('data-schema', 'landing-pricing');
    el.text = JSON.stringify(offerSchema);
    document.head.appendChild(el);
    return () => el.remove();
  }, [plans]);

  // Scroll to section when navigating from another page via hash (e.g. /#features)
  useEffect(() => {
    if (location.hash) {
      const id = location.hash.slice(1);
      setTimeout(() => {
        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
    } else {
      window.scrollTo({ top: 0 });
    }
  }, [location.hash]);

  const copyCode = () => {
    navigator.clipboard.writeText(CODE_SNIPPET);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      <SiteHeader />

      {/* ===== HERO ===== */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        {/* Background glow */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-primary/15 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute top-20 right-0 w-[400px] h-[300px] bg-emerald-500/8 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 pt-20 pb-24 md:pt-28 md:pb-32 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-primary mb-8">
              <Zap className="h-3.5 w-3.5" />
              <span className="text-xs font-semibold tracking-wide uppercase">Real-time Blockchain Verification</span>
            </div>

            <h1 className="text-5xl md:text-7xl font-bold tracking-tight mb-6 leading-[1.08]" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Accept Crypto Payments on{' '}
              <br className="hidden md:block" />
              Your Website{' '}
              <span className="text-gradient">Instantly</span>
            </h1>

            <p className="text-lg md:text-xl text-muted-foreground mb-10 max-w-2xl mx-auto leading-relaxed">
              Start accepting crypto payments directly on your website with CoinPayUp, built for online businesses, e-commerce stores, SaaS platforms, and digital services.
            </p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-16">
              <a href="/#pricing">
                <Button size="lg" className="bg-primary text-white shadow-[0_0_20px_rgba(99,102,241,0.5)] hover:shadow-[0_0_30px_rgba(99,102,241,0.7)] px-8 h-12 text-base font-medium" data-testid="hero-cta-btn">
                  Create Free Account <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </a>
              <a href="#developers">
                <Button size="lg" variant="outline" className="px-8 h-12 text-base border-white/10 hover:bg-white/5">
                  <Terminal className="mr-2 h-4 w-4" /> View API Docs
                </Button>
              </a>
            </div>

            {/* Stats bar */}
            <div className="flex flex-wrap items-center justify-center gap-8 md:gap-16">
              {[
                { value: '$2M+', label: 'Processed' },
                { value: '1,200+', label: 'Merchants' },
                { value: '99.9%', label: 'Uptime' },
                { value: '<3s', label: 'Verification' },
              ].map(stat => (
                <div key={stat.label} className="text-center">
                  <p className="text-2xl md:text-3xl font-bold text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{stat.value}</p>
                  <p className="text-xs text-muted-foreground mt-1 tracking-wide">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ===== HOW IT WORKS ===== */}
      <section id="how-it-works" className="py-24 md:py-32" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">Simple Integration</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Three Steps to Get Paid
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {[
              { step: '01', icon: Wallet, title: 'Connect Your Wallet', desc: 'Add your own crypto wallet address. We never create wallets, store keys, or touch your funds. Fully non-custodial.' },
              { step: '02', icon: Code, title: 'Create Invoice via API', desc: 'Generate payment requests through our REST API or dashboard. Set amount, currency, and expiry. We handle the checkout.' },
              { step: '03', icon: Zap, title: 'Instant Verification', desc: 'Our WebSocket monitors the blockchain in real-time. Payment confirmed in seconds. Webhook fires to your server.' },
            ].map((item, i) => (
              <div key={item.step} className="relative group">
                <div className="glass-card rounded-2xl p-8 h-full hover:-translate-y-1 transition-all duration-300">
                  <div className="flex items-center gap-3 mb-6">
                    <span className="text-3xl font-bold text-primary/30 font-mono" style={{ fontFamily: "'JetBrains Mono'" }}>{item.step}</span>
                    <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center">
                      <item.icon className="h-5 w-5 text-primary" />
                    </div>
                  </div>
                  <h3 className="text-xl font-semibold mb-3 text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
                {/* Connector line */}
                {i < 2 && <div className="hidden md:block absolute top-1/2 -right-3 w-6 border-t border-dashed border-white/10" />}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== BENEFITS ===== */}
      <section id="features" className="py-24 md:py-32 border-y border-border" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400 mb-3">Why CoinPayUp</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Built for Modern Commerce
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: Shield, title: 'Non-Custodial', desc: 'Your keys, your coins. Payments go directly to your wallet. We never hold or access your funds.', accent: 'text-emerald-400 bg-emerald-400/10' },
              { icon: Zap, title: 'Real-time Detection', desc: 'WebSocket-powered blockchain monitoring detects payments within seconds. No polling, no delays.', accent: 'text-primary bg-primary/10' },
              { icon: Globe, title: 'Global Payments', desc: 'Accept crypto from anywhere in the world. No borders, no banking restrictions. 24/7 availability.', accent: 'text-sky-400 bg-sky-400/10' },
              { icon: RefreshCcw, title: 'Zero Chargebacks', desc: 'Blockchain transactions are irreversible. Eliminate fraud and chargeback disputes permanently.', accent: 'text-amber-400 bg-amber-400/10' },
              { icon: CreditCard, title: 'Low Fees', desc: 'Starting at 0.5% per transaction. No setup fees, no monthly minimums. Pay only for what you use.', accent: 'text-violet-400 bg-violet-400/10' },
              { icon: Layers, title: 'Multi-Chain', desc: 'BNB Chain, Ethereum, Polygon, Tron and more. Support any EVM-compatible chain with a single integration.', accent: 'text-pink-400 bg-pink-400/10' },
            ].map(feature => (
              <div key={feature.title}
                className="glass-card rounded-2xl p-7 hover:-translate-y-1 transition-all duration-300 group">
                <div className={`w-11 h-11 rounded-xl ${feature.accent} flex items-center justify-center mb-5`}>
                  <feature.icon className="h-5 w-5" />
                </div>
                <h3 className="text-lg font-semibold mb-2 text-foreground" style={{ fontFamily: "'Outfit', sans-serif" }}>{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== SECURITY & COMPLIANCE ===== */}
      <section className="py-24 md:py-32" data-testid="security-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-emerald-400 mb-3">Security First</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Enterprise-Grade Security
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                CoinPayUp is built with security at its core. We never store private keys, never hold funds, and validate every transaction directly on the blockchain.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Lock, title: 'Non-Custodial Architecture', desc: 'Funds flow directly to your wallet. We only verify — never hold.' },
                  { icon: Shield, title: 'On-Chain Verification', desc: 'Every transaction is validated against the blockchain. No simulation, no shortcuts.' },
                  { icon: RefreshCcw, title: 'Duplicate TX Protection', desc: 'Cross-invoice and per-invoice duplicate detection prevents double-spend fraud.' },
                  { icon: BarChart3, title: 'Real-time Monitoring', desc: 'Circuit breakers, rate limiters, and automated alerts protect your operations.' },
                ].map(item => (
                  <div key={item.title} className="flex gap-4">
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-emerald-400/10 flex items-center justify-center mt-0.5">
                      <item.icon className="h-4.5 w-4.5 text-emerald-400" />
                    </div>
                    <div>
                      <h4 className="text-sm font-semibold text-foreground mb-1">{item.title}</h4>
                      <p className="text-sm text-muted-foreground">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            {/* Visual card */}
            <div className="relative">
              <div className="absolute inset-0 bg-emerald-500/10 rounded-3xl blur-[60px]" />
              <div className="relative glass-card rounded-2xl p-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm text-foreground">Transaction verified on-chain</span>
                    <span className="ml-auto text-xs text-muted-foreground font-mono">0.8s</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm text-foreground">Amount matches invoice</span>
                    <span className="ml-auto text-xs text-emerald-400 font-mono">25.00 USDT</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm text-foreground">Recipient wallet confirmed</span>
                    <span className="ml-auto text-xs text-muted-foreground font-mono truncate max-w-[100px]">0x7f2c...9a4b</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                    <CheckCircle className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm text-foreground">Webhook dispatched</span>
                    <span className="ml-auto text-xs text-emerald-400">200 OK</span>
                  </div>
                  <div className="flex items-center gap-3 p-3 rounded-xl bg-emerald-500/10 border border-emerald-500/20">
                    <Shield className="h-5 w-5 text-emerald-400" />
                    <span className="text-sm font-semibold text-emerald-400">Payment Confirmed &mdash; 12 block confirmations</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== DEVELOPER API ===== */}
      <section id="developers" className="py-24 md:py-32 border-y border-border" data-testid="developers-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div className="lg:sticky lg:top-24">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">For Developers</p>
              <h2 className="text-4xl md:text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
                Ship in Minutes,<br />Not Weeks
              </h2>
              <p className="text-base text-muted-foreground leading-relaxed mb-8">
                Clean REST API, real-time webhooks, and SDKs for every major framework. Create invoices, verify payments, and manage your entire payment flow programmatically.
              </p>
              <div className="grid grid-cols-2 gap-4 mb-8">
                {[
                  { icon: Terminal, label: 'REST API' },
                  { icon: Webhook, label: 'Webhooks' },
                  { icon: Code, label: 'SDKs' },
                  { icon: Layers, label: 'Postman Collection' },
                ].map(item => (
                  <div key={item.label} className="flex items-center gap-3 p-3 rounded-xl bg-muted/50 border border-border">
                    <item.icon className="h-4 w-4 text-primary" />
                    <span className="text-sm font-medium text-foreground">{item.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/login">
                <Button className="bg-primary text-white shadow-[0_0_16px_rgba(99,102,241,0.4)] hover:shadow-[0_0_24px_rgba(99,102,241,0.6)]" data-testid="dev-cta-btn">
                  Get API Key <ArrowRight className="ml-2 h-4 w-4" />
                </Button>
              </Link>
            </div>

            {/* Code block */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden border border-border">
                <div className="flex items-center justify-between px-5 py-3 bg-muted/50 border-b border-border">
                  <div className="flex items-center gap-2">
                    <div className="w-3 h-3 rounded-full bg-red-500/40" />
                    <div className="w-3 h-3 rounded-full bg-yellow-500/40" />
                    <div className="w-3 h-3 rounded-full bg-green-500/40" />
                  </div>
                  <span className="text-xs text-muted-foreground font-mono">create-invoice.js</span>
                  <button onClick={copyCode} className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors" data-testid="copy-code-btn">
                    {copied ? <><Check className="h-3 w-3 text-emerald-400" /> Copied</> : <><Copy className="h-3 w-3" /> Copy</>}
                  </button>
                </div>
                <pre className="p-5 overflow-x-auto text-sm leading-relaxed bg-[#0d1117]" style={{ fontFamily: "'JetBrains Mono', monospace" }}>
                  <code className="text-muted-foreground">
                    {CODE_SNIPPET.split('\n').map((line, i) => (
                      <div key={i} className="whitespace-pre">
                        <span className="text-muted-foreground/30 select-none mr-4 inline-block w-6 text-right">{i + 1}</span>
                        <span dangerouslySetInnerHTML={{ __html: highlightSyntax(line) }} />
                      </div>
                    ))}
                  </code>
                </pre>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ===== TESTIMONIALS ===== */}
      <section className="py-24 md:py-32" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-400 mb-3">Trusted by Builders</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              What Merchants Say
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {TESTIMONIALS.map(t => (
              <div key={t.name} className="glass-card rounded-2xl p-7 flex flex-col">
                <div className="flex gap-1 mb-4">
                  {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />)}
                </div>
                <p className="text-sm text-muted-foreground leading-relaxed flex-1 mb-6">"{t.quote}"</p>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-primary/20 flex items-center justify-center text-xs font-bold text-primary">
                    {t.avatar}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{t.name}</p>
                    <p className="text-xs text-muted-foreground">{t.role}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Trust badges */}
          <div className="flex flex-wrap items-center justify-center gap-8 mt-16">
            {['SOC 2 Compliant', 'Non-Custodial', '99.9% Uptime SLA', 'GDPR Ready'].map(badge => (
              <div key={badge} className="flex items-center gap-2 px-4 py-2 rounded-full bg-muted/50 border border-border">
                <CheckCircle className="h-3.5 w-3.5 text-emerald-400" />
                <span className="text-xs font-medium text-muted-foreground">{badge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ===== PRICING ===== */}
      <section id="pricing" className="py-24 md:py-32 border-y border-border" data-testid="pricing-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary mb-3">Transparent Pricing</p>
            <h2 className="text-4xl md:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Outfit', sans-serif" }}>
              Simple, Predictable Pricing
            </h2>
            <p className="text-base text-muted-foreground mt-4 max-w-lg mx-auto">No hidden fees. No surprise charges. Pay for what you use.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {plans.map((plan, i) => {
              const isPopular = plan.name === 'Starter';
              return (
                <div key={plan.id} data-testid={`pricing-plan-${plan.id}`}
                  className={`relative glass-card rounded-2xl p-8 flex flex-col ${isPopular ? 'ring-1 ring-primary/50 glow-primary' : ''}`}>
                  {isPopular && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full bg-primary text-white text-[10px] font-bold uppercase tracking-wider">
                      Most Popular
                    </div>
                  )}
                  <h3 className="text-xl font-semibold mb-2" style={{ fontFamily: "'Outfit', sans-serif" }}>{plan.name}</h3>
                  <div className="mb-1">
                    <span className="text-4xl font-bold" style={{ fontFamily: "'Outfit', sans-serif" }}>
                      {plan.monthly_price > 0 ? `$${plan.monthly_price}` : 'Free'}
                    </span>
                    {plan.monthly_price > 0 && <span className="text-muted-foreground text-sm">/month</span>}
                  </div>
                  <p className="text-sm text-muted-foreground mb-6">{plan.percent_fee}% per verified transaction</p>
                  <ul className="space-y-3 mb-8 flex-1">
                    <li className="flex items-center gap-2.5 text-sm">
                      <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                      <span>{plan.tx_limit === null ? 'Unlimited' : plan.tx_limit.toLocaleString()} verified TX/month</span>
                    </li>
                    {plan.extra_tx_cost > 0 && (
                      <li className="flex items-center gap-2.5 text-sm">
                        <CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                        <span>${plan.extra_tx_cost} per extra TX</span>
                      </li>
                    )}
                    <li className="flex items-center gap-2.5 text-sm"><CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" /><span>Full dashboard access</span></li>
                    <li className="flex items-center gap-2.5 text-sm"><CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" /><span>Webhook notifications</span></li>
                    <li className="flex items-center gap-2.5 text-sm"><CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" /><span>Multi-chain support</span></li>
                    <li className="flex items-center gap-2.5 text-sm"><CheckCircle className="h-4 w-4 text-emerald-400 flex-shrink-0" /><span>REST API + SDKs</span></li>
                  </ul>
                  <Button onClick={() => navigate(`/register?plan=${plan.id}`)}
                    className={`w-full h-11 font-medium ${isPopular ? 'bg-primary text-white shadow-[0_0_16px_rgba(99,102,241,0.4)]' : 'bg-muted hover:bg-muted/80 text-foreground border border-border'}`}>
                    Get Started <ChevronRight className="ml-1 h-4 w-4" />
                  </Button>
                </div>
              );
            })}
            {plans.length === 0 && (
              <div className="col-span-3 text-center text-muted-foreground py-12">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-3" />
                Loading plans...
              </div>
            )}
          </div>

          <p className="text-center text-sm text-muted-foreground mt-8">
            Need a custom plan? <Link to="/register" className="text-primary hover:underline">Contact us</Link> for enterprise pricing.
          </p>
        </div>
      </section>

      {/* ===== FINAL CTA ===== */}
      <section className="py-24 md:py-32 relative overflow-hidden" data-testid="cta-section">
        <div className="absolute inset-0 bg-primary/5" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-primary/10 rounded-full blur-[100px] pointer-events-none" />
        <div className="max-w-3xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6" style={{ fontFamily: "'Outfit', sans-serif" }}>
            Ready to Accept Crypto?
          </h2>
          <p className="text-lg text-muted-foreground mb-10 max-w-xl mx-auto leading-relaxed">
            Join merchants worldwide using CoinPayUp for seamless, non-custodial crypto payments. Start for free — no credit card required.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a href="/#pricing">
              <Button size="lg" className="bg-primary text-white shadow-[0_0_20px_rgba(99,102,241,0.5)] hover:shadow-[0_0_30px_rgba(99,102,241,0.7)] px-10 h-12 text-base font-medium" data-testid="final-cta-btn">
                Create Free Account <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  );
};

// Simple syntax highlighting for the code snippet
function highlightSyntax(line) {
  return line
    .replace(/\/\/.*/g, '<span style="color:hsl(228 10% 45%)">$&</span>')
    .replace(/'([^']*)'/g, '<span style="color:#10B981">\'$1\'</span>')
    .replace(/`([^`]*)`/g, '<span style="color:#10B981">`$1`</span>')
    .replace(/\b(const|await|let|var|method|headers|body)\b/g, '<span style="color:hsl(246 80% 68%)">$1</span>')
    .replace(/\b(fetch|JSON\.stringify|window|response|json)\b/g, '<span style="color:#F59E0B">$1</span>');
}

export default LandingPage;
